import { useEffect } from 'react';

/**
 * SEO Component for setting page-specific meta tags
 * Usage: <SEO title="Menu" description="View our full menu..." />
 */
const SEO = ({ 
  title, 
  description, 
  keywords,
  canonical,
  type = 'website',
  noindex = false
}) => {
  const baseTitle = "1st Ave ICP - Ice Cream & Pastry | Mineral Wells, TX";
  const baseDescription = "All-natural ice cream made with the highest quality ingredients, specialty sundaes, ice cream stackers, fresh pastries & more! Visit us at 103 NE 1st Ave, Mineral Wells, TX.";
  
  useEffect(() => {
    // Update title
    document.title = title ? `${title} | 1st Ave ICP - Mineral Wells, TX` : baseTitle;
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', description || baseDescription);
    }
    
    // Update OG title
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', title ? `${title} | 1st Ave ICP` : baseTitle);
    }
    
    // Update OG description
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', description || baseDescription);
    }
    
    // Update Twitter title
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', title ? `${title} | 1st Ave ICP` : baseTitle);
    }
    
    // Update Twitter description
    const twitterDescription = document.querySelector('meta[name="twitter:description"]');
    if (twitterDescription) {
      twitterDescription.setAttribute('content', description || baseDescription);
    }
    
    // Update canonical URL
    if (canonical) {
      let canonicalTag = document.querySelector('link[rel="canonical"]');
      if (canonicalTag) {
        canonicalTag.setAttribute('href', `https://1staveicp.com${canonical}`);
      }
    }
    
    // Update OG type
    const ogType = document.querySelector('meta[property="og:type"]');
    if (ogType) {
      ogType.setAttribute('content', type);
    }
    
    // Update keywords if provided
    if (keywords) {
      let keywordsTag = document.querySelector('meta[name="keywords"]');
      if (keywordsTag) {
        keywordsTag.setAttribute('content', keywords);
      }
    }

    // Update robots directive — set noindex for low-value/thin pages
    // ALSO force noindex for legacy ghost URLs (matches the early-shim in
    // public/index.html). This guarantees the rendered SPA never accidentally
    // re-flips index back on for known-bad URLs like /?whoisdatacenter.com.
    const isGhostUrl = (() => {
      try {
        const p = (window.location.pathname || '').toLowerCase();
        const q = (window.location.search || '').toLowerCase();
        const badPath = [
          /^\/wp-admin/, /^\/wp-content/, /^\/wp-includes/, /^\/wp-json/,
          /^\/wp-login/, /^\/xmlrpc\.php/,
          /^\/sample-page/, /^\/category\//, /^\/tag\//, /^\/author\//,
          /^\/feed\//, /^\/comments\//, /^\/trackback\//, /^\/attachment\//,
          /^\/elementor(-hf)?\//,
          /^\/about-us\/?$/,
          /^\/blog\/tag\//, /^\/blog\/category\//,
        ];
        const badQuery = [
          /elementor/, /whoisdatacenter/, /[?&]p=/, /[?&]page_id=/,
          /[?&]attachment_id=/, /[?&]preview=/, /[?&]ver=/,
        ];
        return badPath.some((r) => r.test(p)) || badQuery.some((r) => r.test(q));
      } catch (_) { return false; }
    })();

    const robotsTag = document.querySelector('meta[name="robots"]');
    if (robotsTag) {
      robotsTag.setAttribute(
        'content',
        (noindex || isGhostUrl)
          ? 'noindex, follow'
          : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
      );
    }
    
    // Cleanup - reset to defaults when component unmounts
    return () => {
      document.title = baseTitle;
      // Always reset robots back to default index on unmount so SPA navigation
      // doesn't leave a stale noindex on subsequent pages.
      const rt = document.querySelector('meta[name="robots"]');
      if (rt) rt.setAttribute('content', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    };
  }, [title, description, keywords, canonical, type, noindex]);
  
  return null; // This component doesn't render anything
};

export default SEO;
